import { motion } from 'framer-motion';
import { useState } from 'react';
import { Calendar, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { API } from '@/lib/api';

export default function DemoPage() {
  const [formData, setFormData] = useState({
    name: '',
    school_name: '',
    phone: '',
    email: '',
    requirement: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success('Demo request submitted successfully!');
      setIsSuccess(true);
      setFormData({
        name: '',
        school_name: '',
        phone: '',
        email: '',
        requirement: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting demo form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="demo-page" className="pt-20">
      <Toaster position="top-center" />
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-block bg-emerald-light/10 border border-emerald/20 px-4 py-2 rounded-full mb-6">
              <span className="text-emerald font-medium text-sm">
                Free Demo - No Credit Card Required
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black text-navy mb-6 font-heading">
              Book Your Free Demo
            </h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto">
              See VibhumEduSys in action! Schedule a personalized demo with our team
              and discover how we can transform your school operations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 lg:p-10 rounded-2xl border border-slate-200 shadow-lg"
            >
              <h2 className="text-2xl font-bold text-navy mb-6 font-heading">
                Request Demo Access
              </h2>
              {isSuccess ? (
                <div
                  data-testid="success-message"
                  className="text-center py-12"
                >
                  <CheckCircle className="h-16 w-16 text-emerald mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-navy mb-2">
                    Thank You!
                  </h3>
                  <p className="text-slate-600">
                    We've received your demo request. Our team will contact you
                    within 24 hours to schedule your personalized demo.
                  </p>
                  <Button
                    onClick={() => setIsSuccess(false)}
                    className="mt-6 bg-emerald hover:bg-emerald-light text-white"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="demo-form">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Your Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="demo-name-input"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g. Rajesh Kumar"
                      />
                    </div>

                    <div>
                      <Label htmlFor="school_name">School / Institute Name *</Label>
                      <Input
                        id="school_name"
                        name="school_name"
                        data-testid="demo-school-input"
                        type="text"
                        required
                        value={formData.school_name}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g. Delhi Public School"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="demo-phone-input"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        data-testid="demo-email-input"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="requirement">Your Requirements *</Label>
                      <Textarea
                        id="requirement"
                        name="requirement"
                        data-testid="demo-requirement-input"
                        required
                        value={formData.requirement}
                        onChange={handleChange}
                        className="mt-1"
                        rows={4}
                        placeholder="Tell us about your school size, current challenges, and what you're looking for..."
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="demo-submit-button"
                      disabled={isSubmitting}
                      className="w-full bg-emerald hover:bg-emerald-light text-white font-semibold py-6 text-lg"
                    >
                      {isSubmitting ? 'Submitting...' : 'Schedule My Demo'}
                      {!isSubmitting && <Calendar className="ml-2 h-5 w-5" />}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      By submitting this form, you agree to our Terms of Service
                      and Privacy Policy.
                    </p>
                  </div>
                </form>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col justify-center"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-navy mb-4 font-heading">
                  What to Expect in Your Demo
                </h3>
                <div className="space-y-4">
                  {[
                    'Live walkthrough of VibhumEduSys platform',
                    'Personalized demo based on your school needs',
                    'Q&A session with our product expert',
                    'Discussion on implementation and training',
                    'Custom pricing for your institution',
                  ].map((item, index) => (
                    <div
                      key={index}
                      data-testid={`demo-benefit-${index}`}
                      className="flex items-center space-x-3"
                    >
                      <CheckCircle className="h-6 w-6 text-emerald flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-light/10 border-2 border-emerald/20 rounded-2xl p-6">
                <h4 className="font-semibold text-navy mb-2">
                  Already a Customer?
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  Access your admin dashboard to manage your school operations.
                </p>
                <a href="https://school.vibhumedusys.com/">
                  <Button
                    variant="outline"
                    data-testid="admin-access-button"
                    className="border-2 border-emerald text-emerald hover:bg-emerald/5 font-semibold"
                  >
                    Go to Admin Dashboard
                  </Button>
                </a>
              </div>

              <div className="mt-8 bg-slate-100 rounded-2xl p-6">
                <h4 className="font-semibold text-navy mb-3">Trusted by</h4>
                <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                  <div>
                    <p className="text-2xl font-bold text-emerald">10+</p>
                    <p>Schools</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald">4.5K+</p>
                    <p>Students</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald">100%</p>
                    <p>Satisfaction</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald">5+</p>
                    <p>Team Members</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
